import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output, ViewChild } from '@angular/core';

export type DiwaliIntroVariant = 'guest' | 'buyer' | 'seller';

interface Spark { x: number; y: number; px: number; py: number; vx: number; vy: number; col: string; life: number; ml: number; w: number; }
interface Ring { x: number; y: number; col: string; maxR: number; life: number; ml: number; lw: number; }
interface Glow { x: number; y: number; r: number; col: string; life: number; ml: number; }
interface Ember { x: number; y: number; vx: number; vy: number; col: string; life: number; ml: number; r: number; }

/**
 * Beat22 Diwali Sale intro (shared): full page animation when the sale goes live.
 * A rocket rises and bursts into gold, then the logo, the offer, "Sale is Live Now" and the CTA come in.
 * The page behind is blurred. Closes on Skip / CTA / after 15 seconds.
 * Variants (by role): guest (switches between both offers), buyer (50% off on beats), seller (45% off Pro Annual).
 * Usage: <ipm-diwali-sale-intro variant="buyer" (closed)="..."></ipm-diwali-sale-intro>
 */
@Component({
  selector: 'ipm-diwali-sale-intro',
  templateUrl: './diwali-sale-intro.component.html',
  styleUrls: ['./diwali-sale-intro.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiwaliSaleIntroComponent implements AfterViewInit, OnDestroy {
  @Input() variant: DiwaliIntroVariant = 'guest';
  /** Skip, CTA or the 15 second timeout */
  @Output() closed = new EventEmitter<void>();

  @ViewChild('bgCanvas', { static: true }) bgCanvasRef: ElementRef<HTMLCanvasElement>;
  @ViewChild('fgCanvas', { static: true }) fgCanvasRef: ElementRef<HTMLCanvasElement>;
  @ViewChild('diwaliLogo', { static: true }) diwaliLogoRef: ElementRef<HTMLElement>;

  /** Reveal step: 1 skip, 2 Beat22 logo, 3 Diwali logo, 4 offer, 5 live chip, 6 CTA */
  step: number = 0;
  /** Guest: which offer is showing */
  showSellerOffer: boolean = false;
  isClosing: boolean = false;

  private frameId = 0;
  private offerTimer: ReturnType<typeof setInterval> | null = null;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  private resizeHandler = () => this.resize();
  private width = 0;
  private height = 0;

  private readonly BURST_COLORS = ['#FFD700', '#FFC200', '#FFE566', '#fff', '#FFF8C0', '#F5C518', '#C9A84C', '#EFB90A'];
  private readonly GOLD = ['#FFD700', '#FFC200', '#FFE566', '#FFF8C0', '#F5C518', '#EFB90A', '#fff'];
  // short rocket so the sale text shows right away
  private readonly RISE = 750;
  private readonly BURST = 800;

  constructor(private zone: NgZone, private cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      // no rocket or burst: show everything at once
      this.setStep(6);
      this.startOfferCycle();
    } else {
      this.zone.runOutsideAngular(() => this.startAnimation());
    }
    this.closeTimer = setTimeout(() => this.close(), 15000);
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.frameId);
    window.removeEventListener('resize', this.resizeHandler);
    if (this.offerTimer) clearInterval(this.offerTimer);
    if (this.closeTimer) clearTimeout(this.closeTimer);
  }

  /** Fade out, then tell the parent */
  close(): void {
    if (this.isClosing) return;
    this.isClosing = true;
    this.cdr.markForCheck();
    setTimeout(() => this.closed.emit(), 900);
  }

  private setStep(step: number): void {
    this.zone.run(() => {
      this.step = Math.max(this.step, step);
      this.cdr.markForCheck();
    });
  }

  /** Guest: switch between the buyer and the seller offer every 3.5 seconds */
  private startOfferCycle(): void {
    if (this.variant !== 'guest' || this.offerTimer) return;
    this.zone.run(() => {
      this.offerTimer = setInterval(() => {
        this.showSellerOffer = !this.showSellerOffer;
        this.cdr.markForCheck();
      }, 3500);
    });
  }

  private resize(): void {
    this.width = window.innerWidth;
    this.height = window.innerHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    [this.bgCanvasRef.nativeElement, this.fgCanvasRef.nativeElement].forEach((canvas) => {
      canvas.width = Math.round(this.width * dpr);
      canvas.height = Math.round(this.height * dpr);
      canvas.getContext('2d')?.setTransform(dpr, 0, 0, dpr, 0, 0);
    });
  }

  private startAnimation(): void {
    const bg = this.bgCanvasRef.nativeElement.getContext('2d');
    const fg = this.fgCanvasRef.nativeElement.getContext('2d');
    if (!bg || !fg) {
      this.setStep(6);
      return;
    }
    this.resize();
    window.addEventListener('resize', this.resizeHandler);

    const rnd = (a: number, b: number) => a + (b - a) * Math.random();
    const pick = (list: Array<string>) => list[Math.floor(Math.random() * list.length)];
    let sparks: Array<Spark> = [];
    let rings: Array<Ring> = [];
    let glows: Array<Glow> = [];
    let embers: Array<Ember> = [];
    let fountain: { x: number; y: number; t: number } | null = null;
    let burstFired = false;
    let darkness = 0;
    let darkTarget = 0;
    let start = 0;
    let last = 0;

    const A = this.BURST;
    const timeline: Array<{ ms: number; fn: () => void; done?: boolean }> = [
      { ms: 0, fn: () => this.setStep(1) },
      { ms: A, fn: () => { darkTarget = 0.55; this.setStep(2); } },
      { ms: A + 150, fn: () => { this.setStep(3); setTimeout(() => (fountain = this.fountainOrigin()), 600); } },
      { ms: A + 550, fn: () => { this.setStep(4); this.startOfferCycle(); } },
      { ms: A + 900, fn: () => this.setStep(5) },
      { ms: A + 1200, fn: () => this.setStep(6) },
    ];

    const rocketPos = (ms: number) => {
      const ease = (t: number) => 1 - Math.pow(1 - t, 3);
      const p = Math.min(1, ms / this.RISE);
      return { x: this.width / 2 + Math.sin(ms * 0.004) * 3 * (1 - p), y: this.height + 70 + (this.height * 0.22 - this.height - 70) * ease(p) };
    };

    const drawRocket = (x: number, y: number) => {
      bg.save();
      bg.translate(x, y);
      bg.rotate(-0.04);
      bg.strokeStyle = '#7B5B1E';
      bg.lineWidth = 2.5;
      bg.beginPath(); bg.moveTo(0, 8); bg.lineTo(0, 52); bg.stroke();
      const gradient = bg.createLinearGradient(-5, 0, 5, 0);
      gradient.addColorStop(0, '#CC2200');
      gradient.addColorStop(1, '#FF4422');
      bg.fillStyle = gradient;
      bg.shadowColor = '#FF4422';
      bg.shadowBlur = 10;
      bg.fillRect(-5, -6, 10, 22);
      bg.fillStyle = '#FFD700';
      bg.shadowColor = '#FFD700';
      bg.fillRect(-5, 11, 10, 4);
      bg.beginPath(); bg.moveTo(0, -20); bg.lineTo(-5, -6); bg.lineTo(5, -6); bg.closePath(); bg.fill();
      bg.restore();
    };

    const burst = (x: number, y: number) => {
      rings.push({ x, y, col: 'rgba(255,255,255,0.9)', maxR: this.width * 0.46, life: 36, ml: 36, lw: 10 });
      rings.push({ x, y, col: '#FFD700', maxR: this.width * 0.33, life: 54, ml: 54, lw: 4 });
      rings.push({ x, y, col: 'rgba(255,194,0,0.7)', maxR: this.width * 0.58, life: 70, ml: 70, lw: 2 });
      for (let i = 0; i < 8; i++) glows.push({ x, y, r: rnd(12, 50), col: '#fff', life: rnd(8, 16), ml: 16 });
      for (let i = 0; i < 10; i++) glows.push({ x, y, r: rnd(4, 18), col: '#FFD700', life: rnd(12, 22), ml: 22 });
      // fewer sparks on small screens
      const count = this.width < 600 ? 160 : 280;
      const addSparks = (n: number, speed: [number, number], col: () => string, life: [number, number], w: [number, number]) => {
        for (let i = 0; i < n; i++) {
          const angle = rnd(0, Math.PI * 2);
          const v = rnd(speed[0], speed[1]);
          const lifespan = rnd(life[0], life[1]);
          sparks.push({ x, y, px: x, py: y, vx: Math.cos(angle) * v, vy: Math.sin(angle) * v, col: col(), life: lifespan, ml: lifespan, w: rnd(w[0], w[1]) });
        }
      };
      addSparks(count, [2, 15], () => pick(this.BURST_COLORS), [70, 120], [1.5, 3.5]);
      addSparks(50, [0.5, 5], () => '#ffffff', [24, 44], [1.5, 2.5]);
      addSparks(40, [5, 16], () => '#FFE566', [45, 85], [2, 4]);
    };

    // soft glow without shadowBlur (shadowBlur on every particle drops frames): a wide faint dot under a small bright one
    const dot = (ctx: CanvasRenderingContext2D, x: number, y: number, r: number, col: string, a: number) => {
      ctx.fillStyle = col;
      ctx.globalAlpha = a * 0.18;
      ctx.beginPath(); ctx.arc(x, y, r * 3, 0, Math.PI * 2); ctx.fill();
      ctx.globalAlpha = a;
      ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
    };

    const loop = (ts: number) => {
      this.frameId = requestAnimationFrame(loop);
      if (!start) start = last = ts;
      const ms = ts - start;
      // movement is per 60fps frame, scaled by the real frame time: same speed on 60 / 120hz and when frames drop
      const k = Math.min(3, (ts - last) / (1000 / 60));
      last = ts;
      timeline.forEach((item) => {
        if (!item.done && ms >= item.ms) {
          item.done = true;
          item.fn();
        }
      });
      // ease the dark backdrop in
      darkness += (darkTarget - darkness) * Math.min(1, 0.04 * k);
      bg.clearRect(0, 0, this.width, this.height);
      if (darkness > 0.001) {
        bg.globalAlpha = darkness;
        bg.fillStyle = '#000';
        bg.fillRect(0, 0, this.width, this.height);
        bg.globalAlpha = 1;
      }
      // rocket trail and rocket
      if (ms < this.RISE + 60) {
        const pos = rocketPos(ms);
        if (Math.random() < Math.min(1, k)) {
          for (let i = 0; i < 2; i++) glows.push({ x: pos.x + rnd(-3, 3), y: pos.y + rnd(6, 18), r: rnd(1.5, 3.5), col: '#FFD700', life: rnd(14, 24), ml: 24 });
        }
        if (ms < this.BURST) drawRocket(pos.x, pos.y);
      }
      if (ms >= this.BURST && !burstFired) {
        burstFired = true;
        burst(this.width / 2, this.height * 0.22);
      }

      bg.save();
      bg.globalCompositeOperation = 'lighter';
      bg.lineCap = 'round';
      const drag = Math.pow(0.975, k);
      sparks = sparks.filter((s) => {
        s.px = s.x; s.py = s.y;
        s.vx *= drag; s.vy = s.vy * drag + 0.16 * k;
        s.x += s.vx * k; s.y += s.vy * k; s.life -= k;
        const a = Math.max(0, s.life / s.ml);
        const w = Math.max(0.5, s.w * Math.max(0.4, a));
        bg.strokeStyle = s.col;
        // faint wide stroke for the glow, then the bright line
        bg.globalAlpha = a * 0.22;
        bg.lineWidth = w * 3.5;
        bg.beginPath(); bg.moveTo(s.px, s.py); bg.lineTo(s.x, s.y); bg.stroke();
        bg.globalAlpha = Math.min(1, a * 1.1);
        bg.lineWidth = w;
        bg.stroke();
        return s.life > 0;
      });
      glows = glows.filter((g) => {
        g.life -= k;
        const a = g.life / g.ml;
        if (a > 0) dot(bg, g.x, g.y, Math.max(0.1, g.r * a), g.col, a * 0.7);
        return g.life > 0;
      });
      rings = rings.filter((r) => {
        r.life -= k;
        const t = 1 - Math.max(0, r.life) / r.ml;
        // ease out: fast start, slows as it grows
        const radius = r.maxR * (1 - Math.pow(1 - t, 2.4));
        const a = Math.pow(Math.max(0, r.life) / r.ml, 1.8) * 0.85;
        if (radius > 0 && a > 0) {
          bg.strokeStyle = r.col;
          bg.globalAlpha = a * 0.25;
          bg.lineWidth = Math.max(0.1, r.lw * (1 - t * 0.5)) * 4;
          bg.beginPath(); bg.arc(r.x, r.y, radius, 0, Math.PI * 2); bg.stroke();
          bg.globalAlpha = a;
          bg.lineWidth = Math.max(0.1, r.lw * (1 - t * 0.5));
          bg.stroke();
        }
        return r.life > 0;
      });
      bg.restore();

      // gold fountain from the top of the Diwali logo
      fg.clearRect(0, 0, this.width, this.height);
      if (fountain) {
        fountain.t += k;
        // ~1.5 embers per 60fps frame
        let spawn = 1.5 * k;
        while (spawn > 0) {
          if (spawn >= 1 || Math.random() < spawn) {
            const angle = -Math.PI / 2 + rnd(-Math.PI * 0.42, Math.PI * 0.42);
            const v = rnd(1.5, 5);
            embers.push({ x: fountain.x, y: fountain.y, vx: Math.cos(angle) * v, vy: Math.sin(angle) * v, col: pick(this.GOLD), life: rnd(30, 58), ml: 58, r: rnd(1.2, 2.6) });
          }
          spawn--;
        }
        fg.save();
        fg.globalCompositeOperation = 'lighter';
        embers = embers.filter((p) => {
          p.x += p.vx * k; p.vy += 0.12 * k; p.y += p.vy * k; p.life -= k;
          const a = p.life / p.ml;
          if (a > 0) dot(fg, p.x, p.y, Math.max(0.1, p.r * a), p.col, a * 0.9);
          return p.life > 0;
        });
        fg.restore();
      }
    };
    this.frameId = requestAnimationFrame(loop);
  }

  private fountainOrigin(): { x: number; y: number; t: number } | null {
    const rect = this.diwaliLogoRef?.nativeElement.getBoundingClientRect();
    return rect ? { x: rect.left + rect.width / 2, y: rect.top + 6, t: 0 } : null;
  }
}
